import { useRef, useState } from 'react';
import { useAuth0AdministrationApi } from '../services/auth0AdministrationService';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

export function Auth0UserPassword({ userId, editable, disabled, onBusyChange }: {
  userId: string; editable: boolean; disabled: boolean; onBusyChange: (busy: boolean) => void;
}) {
  const api = useAuth0AdministrationApi();
  const working = useRef(false);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const update = async () => {
    if (working.current || disabled || !editable || !password || password !== confirmation) return;
    working.current = true; setBusy(true); onBusyChange(true); setError(''); setSaved(false);
    const nextPassword = password;
    setPassword(''); setConfirmation('');
    try { await api.updatePassword(userId, nextPassword); setSaved(true); }
    catch { setError('The password update could not be confirmed. Check the Auth0 password policy and the backend’s update:users grant. If the connection was interrupted, the change may already have completed.'); }
    finally { working.current = false; setBusy(false); onBusyChange(false); }
  };
  return <section className="space-y-3 rounded border p-3 mp-block" data-analytics-private="true" aria-label="User password">
    <h3 className="font-semibold">Password</h3>
    {!editable ? <p className="text-sm">This user’s password is managed by their identity provider.</p> : <>
      <p className="text-sm text-muted-foreground">Set a new password for the selected user. Auth0’s password policy applies. This action does not send a reset email; share the new password securely.</p>
      <Label htmlFor="managed-user-password">New password</Label>
      <Input id="managed-user-password" type="password" autoComplete="new-password" maxLength={128} disabled={disabled} value={password} onChange={e => { setPassword(e.target.value); setSaved(false); }} />
      <Label htmlFor="managed-user-password-confirm">Confirm new password</Label>
      <Input id="managed-user-password-confirm" type="password" autoComplete="new-password" maxLength={128} disabled={disabled} value={confirmation} onChange={e => setConfirmation(e.target.value)} />
      {confirmation && password !== confirmation && <p role="alert" className="text-sm text-destructive">Passwords do not match.</p>}
      <Button type="button" variant="outline" disabled={disabled || !password || password !== confirmation} onClick={() => void update()}>{busy ? 'Updating password…' : 'Update password'}</Button>
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      {saved && <p role="status" className="text-sm">Password updated in Auth0.</p>}
    </>}
  </section>;
}
